import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useState} from "react";
import {Link} from "react-router-dom";
import {
    Building2,
    CircleDollarSign,
    Copy,
    GitBranch,
    KeyRound,
    RotateCcw,
    Send,
    Store,
    Ticket,
    Users,
} from "lucide-react";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {useTranslation} from "../../shared/i18n";
import {formatDateTime, formatMoney, formatNumber} from "../../shared/utils/format";
import {
    createDeployerInvite,
    loadDeployerDashboard,
    loadDeployerLicenses,
    loadDeployerPartnerStaff,
    requestDeployerLicenses,
} from "./api";

const Metric = ({label, value, note, icon: Icon, money = false, loading = false}) => {
    const {t} = useTranslation();
    return (
    <article className="admin-card admin-metric-card">
        <div className="admin-metric-topline">
            <span>{label}</span>
            <Icon size={17} />
        </div>
        <p className={`admin-card-value ${money ? "is-money" : ""}`}>
            {loading ? "—" : money ? formatMoney(value) : formatNumber(value)}
        </p>
        <p className="admin-card-note">{loading ? t("orgs.common.loadingData") : note}</p>
    </article>
    );
};

const copyText = async (value) => {
    if (!value) {
        return false;
    }
    try {
        await navigator.clipboard.writeText(value);
        return true;
    } catch {
        return false;
    }
};

export const DeployerOverviewPage = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [inviteNotice, setInviteNotice] = useState("");
    const [licenseQty, setLicenseQty] = useState("1");
    const capabilities = useCapabilities();
    const canRequestLicenses = capabilities.user?.role === ADMIN_ROLES.DEPLOYER;
    const {data, isLoading, isError, refetch} = useQuery({
        queryKey: ["deployer-dashboard"],
        queryFn: loadDeployerDashboard,
    });
    const licensesQuery = useQuery({
        queryKey: ["deployer-licenses"],
        queryFn: loadDeployerLicenses,
        retry: false,
    });
    const [selectedPlaceId, setSelectedPlaceId] = useState(null);
    const staffQuery = useQuery({
        queryKey: ["deployer-staff", selectedPlaceId],
        queryFn: () => loadDeployerPartnerStaff(selectedPlaceId),
        enabled: Boolean(selectedPlaceId),
        retry: false,
    });
    const requestLicenses = useMutation({
        mutationFn: (quantity) => requestDeployerLicenses(quantity),
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: ["deployer-licenses"]});
            setInviteNotice(t("orgs.deployer.requested"));
        },
        onError: (error) => {
            setInviteNotice(error?.response?.data?.message || t("orgs.deployer.requestFailed"));
        },
    });
    const createInvite = useMutation({
        mutationFn: () => createDeployerInvite({max_uses: 1}),
        onSuccess: async (invite) => {
            await queryClient.invalidateQueries({queryKey: ["deployer-dashboard"]});
            const copied = await copyText(invite?.invite_url);
            setInviteNotice(
                copied
                    ? t("orgs.deployer.copied")
                    : invite?.invite_url || t("orgs.deployer.created")
            );
        },
        onError: (error) => {
            setInviteNotice(error?.response?.data?.message || t("orgs.deployer.createFailed"));
        },
    });

    if (isError) {
        return (
            <div className="admin-page">
                <DataState
                    title={t("orgs.deployer.errorTitle")}
                    copy={t("orgs.deployer.errorCopy")}
                />
                <button className="admin-button admin-button-secondary" type="button" onClick={() => refetch()}>
                    <RotateCcw size={16} /> {t("orgs.common.retry")}
                </button>
            </div>
        );
    }

    const deployer = data?.deployer || {};
    const summary = data?.summary || {};
    const onboarding = data?.onboarding || {};

    const partnerColumns = [
        {
            key: "name",
            label: t("orgs.common.place"),
            render: (row) => (
                <Link className="admin-table-link" to={`/employers/${row.id}`}>
                    <strong>{row.name || t("orgs.common.placeNumber", {id: row.id})}</strong>
                </Link>
            ),
        },
        {key: "city", label: t("orgs.common.city"), render: (row) => row.city || "—"},
        {key: "branch_count", label: t("orgs.common.branches"), render: (row) => formatNumber(row.branch_count)},
        {key: "payments_count", label: t("orgs.common.purchases"), render: (row) => formatNumber(row.payments_count)},
        {key: "payments_day_count", label: t("orgs.deployer.day"), render: (row) => formatNumber(row.payments_day_count)},
        {key: "payments_week_count", label: t("orgs.deployer.week"), render: (row) => formatNumber(row.payments_week_count)},
        {key: "payments_month_count", label: t("orgs.deployer.month"), render: (row) => formatNumber(row.payments_month_count)},
        {key: "gross_amount", label: t("orgs.common.turnover"), render: (row) => <strong>{formatMoney(row.gross_amount)}</strong>},
        {
            key: "status",
            label: t("orgs.common.status"),
            render: (row) => (
                <StatusBadge tone={row.is_active && row.is_shown ? "success" : "warning"}>
                    {row.is_active && row.is_shown ? t("orgs.deployer.active") : t("orgs.deployer.limited")}
                </StatusBadge>
            ),
        },
        {
            key: "staff",
            label: t("orgs.common.staff"),
            render: (row) => (
                <button className="admin-inline-link" type="button" onClick={() => setSelectedPlaceId(row.id)}>
                    {t("orgs.deployer.staff")}
                </button>
            ),
        },
    ];

    const applicationColumns = [
        {key: "display_name", label: t("orgs.deployer.application"), render: (row) => row.display_name || t("orgs.common.applicationNumber", {id: row.id})},
        {key: "phone_number", label: t("orgs.common.phone")},
        {key: "city", label: t("orgs.common.city")},
        {key: "created_at", label: t("orgs.deployer.createdAt"), render: (row) => formatDateTime(row.created_at)},
        {key: "status", label: t("orgs.common.status"), render: (row) => <StatusBadge tone="warning">{row.status}</StatusBadge>},
    ];

    const inviteColumns = [
        {key: "code", label: t("orgs.deployer.code"), render: (row) => <strong>{row.code}</strong>},
        {
            key: "invite_url",
            label: t("orgs.deployer.link"),
            render: (row) =>
                row.invite_url ? (
                    <button className="admin-inline-link" type="button" onClick={() => copyText(row.invite_url)}>
                        <Copy size={14} /> {t("orgs.deployer.copy")}
                    </button>
                ) : (
                    "—"
                ),
        },
        {key: "uses_count", label: t("orgs.deployer.uses"), render: (row) => `${formatNumber(row.uses_count)} / ${row.max_uses ?? "∞"}`},
        {key: "created_at", label: t("orgs.common.created"), render: (row) => formatDateTime(row.created_at)},
        {key: "is_active", label: t("orgs.common.status"), render: (row) => <StatusBadge tone={row.is_active ? "success" : "neutral"}>{row.is_active ? t("orgs.common.active") : t("orgs.deployer.closed")}</StatusBadge>},
    ];

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("orgs.deployer.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("orgs.deployer.title")}</h1>
                    <p className="admin-page-copy">
                        <strong>{deployer.name || deployer.username || t("orgs.deployer.fallbackName")}</strong>
                        {deployer.country_name ? t("orgs.deployer.countryMark", {country: deployer.country_name}) : ""}
                        {t("orgs.deployer.intro")}
                    </p>
                </div>
                <div className="admin-page-actions">
                    <button
                        className="admin-button admin-button-secondary"
                        type="button"
                        disabled={createInvite.isPending}
                        onClick={() => createInvite.mutate()}
                    >
                        <KeyRound size={16} /> {t("orgs.deployer.createInvite")}
                    </button>
                    {canRequestLicenses ? (
                        <>
                            <label className="admin-field">
                                <span>{t("orgs.deployer.requestQuantity")}</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="99"
                                    inputMode="numeric"
                                    value={licenseQty}
                                    disabled={requestLicenses.isPending || Boolean(licensesQuery.data?.pending_request)}
                                    onChange={(event) => setLicenseQty(event.target.value)}
                                />
                            </label>
                            <button
                                className="admin-button admin-button-secondary"
                                type="button"
                                disabled={requestLicenses.isPending || Boolean(licensesQuery.data?.pending_request)}
                                onClick={() => {
                                    const quantity = Math.min(99, Math.max(1, Number.parseInt(licenseQty, 10) || 1));
                                    setLicenseQty(String(quantity));
                                    requestLicenses.mutate(quantity);
                                }}
                            >
                                <Ticket size={16} /> {t("orgs.deployer.requestLicenses")}
                            </button>
                        </>
                    ) : null}
                    <Link className="admin-button admin-button-primary" to="/employers/add">
                        <Store size={16} /> {t("orgs.deployer.newPlace")}
                    </Link>
                </div>
            </header>

            <section className="admin-grid cols-4">
                <Metric label={t("orgs.deployer.assigned")} value={summary.assigned_places_count} note={t("orgs.deployer.activeNote", {count: summary.active_places_count || 0})} icon={Building2} loading={isLoading} />
                <Metric label={t("orgs.common.branches")} value={summary.branches_count} note={t("orgs.deployer.allBranches")} icon={GitBranch} loading={isLoading} />
                <Metric label={t("orgs.deployer.partnerTurnover")} value={summary.gross_amount} note={t("orgs.deployer.paymentsNote", {count: summary.successful_payments_count || 0})} icon={CircleDollarSign} money loading={isLoading} />
                <Metric label={t("orgs.deployer.applications")} value={summary.onboarding_applications_count} note={t("orgs.deployer.moderationNote", {count: summary.onboarding_under_moderation_count || 0})} icon={Send} loading={isLoading} />
                <Metric
                    label={t("orgs.deployer.licenses")}
                    value={licensesQuery.data?.limited ? (licensesQuery.data?.remaining ?? 0) : (licensesQuery.data?.used ?? 0)}
                    note={
                        licensesQuery.data?.pending_request
                            ? t("orgs.deployer.pendingRequest", {count: licensesQuery.data.pending_request.quantity})
                            : licensesQuery.data?.limited
                                ? t("orgs.deployer.licensesUsed", {
                                    used: licensesQuery.data.used || 0,
                                    quota: licensesQuery.data.quota || 0,
                                })
                                : t("orgs.deployer.licensesUnlimited")
                    }
                    icon={Ticket}
                    loading={licensesQuery.isLoading}
                />
            </section>

            <section className="admin-grid cols-2">
                <article className="admin-card">
                    <p className="admin-card-title"><Send size={14} /> {t("orgs.deployer.funnel")}</p>
                    <div className="admin-ledger-rows">
                        <div><span>{t("orgs.deployer.applicationsTotal")}</span><strong>{formatNumber(summary.onboarding_applications_count)}</strong></div>
                        <div><span>{t("orgs.deployer.onModeration")}</span><strong>{formatNumber(summary.onboarding_under_moderation_count)}</strong></div>
                        <div><span>{t("orgs.deployer.published")}</span><strong>{formatNumber(summary.onboarding_published_count)}</strong></div>
                    </div>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><KeyRound size={14} /> {t("orgs.deployer.invites")}</p>
                    <div className="admin-ledger-rows">
                        <div><span>{t("orgs.deployer.codesCreated")}</span><strong>{formatNumber(summary.invites_count)}</strong></div>
                        <div><span>{t("orgs.deployer.uses")}</span><strong>{formatNumber(summary.invite_uses_count)}</strong></div>
                        <div><span>{t("orgs.deployer.activeCodes")}</span><strong>{formatNumber((data?.invites || []).filter((item) => item.is_active).length)}</strong></div>
                    </div>
                </article>
            </section>

            <section className="admin-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title"><Building2 size={14} /> {t("orgs.deployer.myPartners")}</p>
                        <p className="admin-card-note">{t("orgs.deployer.myPartnersNote")}</p>
                    </div>
                    <Link className="admin-inline-link" to="/employers">{t("orgs.deployer.allPlaces")}</Link>
                </div>
                <AdminTable
                    columns={partnerColumns}
                    rows={data?.partners || []}
                    loading={isLoading}
                    emptyText={t("orgs.deployer.partnersEmpty")}
                />
            </section>
            {selectedPlaceId ? (
                <section className="admin-card">
                    <div className="admin-chart-header">
                        <div>
                            <p className="admin-card-title"><Users size={14} /> {t("orgs.deployer.staffTitle")}</p>
                            <p className="admin-card-note">{staffQuery.data?.place_name || t("orgs.deployer.staff")}</p>
                        </div>
                    </div>
                    <AdminTable
                        columns={[
                            {key: "name", label: t("orgs.common.staff"), render: (row) => row.name || "—"},
                            {key: "branch_name", label: t("orgs.common.branch"), render: (row) => row.branch_name || "—"},
                            {key: "transactions_count", label: t("orgs.common.purchases"), render: (row) => formatNumber(row.transactions_count)},
                            {key: "guests_count", label: t("orgs.deployer.guests"), render: (row) => formatNumber(row.guests_count)},
                            {key: "check_percent", label: t("orgs.deployer.checkPercent"), render: (row) => `${formatNumber(row.check_percent)}%`},
                            {key: "earned_amount", label: t("orgs.deployer.earned"), render: (row) => formatMoney(row.earned_amount)},
                            {
                                key: "referral_sum",
                                label: t("orgs.deployer.referral"),
                                render: (row) => (row.referral_enabled ? formatMoney(row.referral_sum) : "—"),
                            },
                        ]}
                        rows={staffQuery.data?.results || []}
                        loading={staffQuery.isLoading}
                        emptyText={t("orgs.deployer.staffEmpty")}
                    />
                </section>
            ) : null}

            <section className="admin-grid cols-2">
                <article className="admin-card">
                    <p className="admin-card-title"><Send size={14} /> {t("orgs.deployer.latest")}</p>
                    <AdminTable columns={applicationColumns} rows={onboarding.latest_applications || []} loading={isLoading} emptyText={t("orgs.deployer.applicationsEmpty")} />
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><KeyRound size={14} /> {t("orgs.deployer.partnerInvites")}</p>
                    {inviteNotice ? <p className="admin-card-note">{inviteNotice}</p> : null}
                    <AdminTable columns={inviteColumns} rows={data?.invites || []} loading={isLoading} emptyText={t("orgs.deployer.invitesEmpty")} />
                </article>
            </section>
        </div>
    );
};
